import { useMutation } from "@apollo/client";
import { Fragment } from "react";
import { Disclosure, Menu, Transition } from "@headlessui/react";
import {
  Bars3Icon,
  BellIcon,
  ServerStackIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import LogoIcon from "../../../assets/icons/LogoIcon";
import SlideoutComponent from "./components/Slideout";
import ReportNotifications from "./components/ReportNotifications";
import { useHandleLogout } from "../../../util";

import useUser from "../../../hooks/useUser";
import { GENERATE_USER_TOKEN } from "../../../graphql/mutation/User";

import { Avatar } from "../Avatar";
import { Button } from "../Button";
import { getEnvironmentBranding } from "../../../config";

export const Header = props => {
  const { className = "", setOpen, showNavigationToggle = true } = props || {};
  const { user: loggedUser, hasPermission } = useUser();
  const handleLogout = useHandleLogout();
  const [generateUserToken] = useMutation(GENERATE_USER_TOKEN);

  const handleGenerateToken = () => {
    generateUserToken().then(({ data }) => {
      const { token } = data.generateUserToken;
      navigator.clipboard.writeText(token);
    });
  };

  const user = {
    name: loggedUser?.meta?.name,
    email: loggedUser?.email,
    imageUrl: loggedUser?.meta?.profilePic,
  };

  const classNames = (...classes) => classes.filter(Boolean).join(" ");

  const userNavigation = [
    { identifier: "account", name: loggedUser?.email, href: "#" },
    {
      identifier: "generate-token",
      name: "Generate Token",
      onClick: handleGenerateToken,
    },
    {
      identifier: "sign-out",
      name: "Sign out",
      href: "#",
      onClick: () => handleLogout(),
    },
  ];

  const isAdmin = hasPermission("ADMIN_MODULE");
  const envOptions = [
    { label: "Local", value: "http://localhost:3000" },
    { label: "Dev", value: "https://dev.new.inventory.whitecube.com" },
    { label: "Preprod", value: "https://preprod.new.inventory.whitecube.com" },
    { label: "Prod", value: "https://new.inventory.whitecube.com" },
  ];
  const currentOrigin =
    typeof window !== "undefined" ? window.location.origin : "";
  const handleEnvChange = nextOrigin => {
    if (!nextOrigin || nextOrigin === currentOrigin) return;
    const path =
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}${window.location.hash}`
        : "";
    window.location.href = `${nextOrigin}${path}`;
  };
  const getEnvHref = nextOrigin => {
    if (!nextOrigin) return "#";
    const path =
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}${window.location.hash}`
        : "";
    return `${nextOrigin}${path}`;
  };

  const { badgeClassName, badgeLabel, isNonProduction, mastheadClassName } =
    getEnvironmentBranding();
  const badgeClass = classNames(
    "ml-2 rounded-full px-2 py-1 text-xs text-black",
    badgeClassName,
  );
  const headerIconButtonClass =
    "rounded-full p-1 text-gray-700 shadow-[inset_0_0_0_2px_rgb(55,65,81)] hover:shadow-[inset_0_0_0_2px_rgb(107,114,128)] hover:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500";
  // Sits above the sticky PrimaryNav (z-30) so the header dropdowns aren't
  // clipped behind it; stays below modal overlays (z-50).
  const headerMenuPanelClass =
    "absolute right-0 z-40 mt-2 origin-top-right cursor-pointer rounded border border-gray-300 bg-white py-1 shadow-lg focus:outline-none";

  return (
    <Disclosure
      as="nav"
      className={`relative z-40 flex-shrink-0 border-b border-gray-200 ${mastheadClassName} ${className}`}
    >
      {() => (
        <>
          <div className="mx-auto px-3 sm:px-4 lg:px-6">
            <div className="flex h-16 justify-between">
              <div className="flex min-w-0 items-center">
                <button
                  type="button"
                  className={`mr-2 inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 lg:hidden ${
                    showNavigationToggle
                      ? "hover:bg-gray-100"
                      : "pointer-events-none opacity-30"
                  }`}
                  aria-label="Open navigation"
                  onClick={() => setOpen?.(true)}
                  disabled={!showNavigationToggle}
                >
                  <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                </button>
                <div className="flex flex-shrink-0 items-center">
                  <Link
                    to="/"
                    aria-label="Home"
                    className="font-semi-bold capitalise min-w-0 text-2xl tracking-widest sm:ml-2"
                  >
                    <LogoIcon className="w-[140px] sm:w-[170px] lg:w-[200px]" />
                  </Link>
                  {isNonProduction && (
                    <span className={`${badgeClass} hidden sm:inline-flex`}>
                      {badgeLabel}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-shrink-0 items-center">
                <div className="mr-1 sm:mr-3">
                  <SlideoutComponent />
                </div>
                {isAdmin && (
                  <Menu as="div" className="relative top-[2px] mr-1 sm:mr-3">
                    <Menu.Button
                      className={headerIconButtonClass}
                      title="Switch environment"
                    >
                      <span className="sr-only">Switch environment</span>
                      <ServerStackIcon className="h-6 w-6" aria-hidden="true" />
                    </Menu.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className={`${headerMenuPanelClass} w-40`}>
                        {envOptions.map(option => (
                          <Menu.Item key={option.value}>
                            {({ active }) => (
                              <a
                                href={getEnvHref(option.value)}
                                onClick={() => handleEnvChange(option.value)}
                                className={classNames(
                                  active ? "bg-gray-100" : "",
                                  "block w-full truncate px-3 py-2 text-left text-xs text-gray-700",
                                )}
                                title={option.label}
                              >
                                {option.label}
                              </a>
                            )}
                          </Menu.Item>
                        ))}
                      </Menu.Items>
                    </Transition>
                  </Menu>
                )}
                <ReportNotifications
                  buttonClassName={headerIconButtonClass}
                  panelClassName={headerMenuPanelClass}
                />
                <div id="header-support-action" className="hidden sm:block" />

                {/* Profile dropdown */}
                <Menu as="div" className="relative ml-2">
                  <Menu.Button className="group flex max-w-xs items-center rounded-full p-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500">
                    <span className="sr-only">Open user menu</span>
                    <Avatar
                      src={user.imageUrl}
                      label={user.name || "User Name"}
                      size="sm-2"
                      className="shadow-none"
                    />
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className={`${headerMenuPanelClass} w-64`}>
                      {userNavigation?.map(item => (
                        <Menu.Item key={item.identifier}>
                          {({ active }) => (
                            <a
                              href={item.href}
                              onClick={() => item?.onClick?.()}
                              className={classNames(
                                active ? "bg-gray-100" : "",
                                "block truncate px-4 py-2 text-sm text-gray-700",
                              )}
                              title={item.name}
                            >
                              {item.name}
                            </a>
                          )}
                        </Menu.Item>
                      ))}
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>

          <Disclosure.Panel>
            <div className="border-t border-gray-200 pb-3 pt-4">
              <div className="flex items-center px-4">
                <div className="flex-shrink-0">
                  <Avatar
                    src={user.imageUrl}
                    label={user.name || "User Name"}
                    size="md"
                  />
                </div>
                <div className="ml-3">
                  <div className="text-base font-medium text-gray-800">
                    {user.name}
                  </div>
                </div>
                <button
                  type="button"
                  className="ml-auto flex-shrink-0 rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500"
                >
                  <span className="sr-only">View notifications</span>
                  <BellIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div className="mt-3 space-y-1">
                {userNavigation?.map(item => (
                  <Disclosure.Button
                    key={item.name}
                    as="a"
                    href={item.href}
                    className="block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                  >
                    {item.name}
                  </Disclosure.Button>
                ))}
              </div>
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
};

export const SectionHeader = props => {
  const { title, subtitle, actions } = props;
  return (
    <div className="pb-4">
      <div className="flex w-full flex-col gap-4 px-4 py-5 font-bold sm:px-6 md:flex-row md:items-start md:justify-between lg:px-8">
        <div className="min-w-0 flex-1">
          <div className="break-words text-xl sm:text-2xl">{title}</div>
          {subtitle && <p className="my-4 font-medium">{subtitle}</p>}
        </div>
        <div
          className={`flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-end ${(actions?.length > 1 || actions?.[0]?.component.length > 1) && "sm:flex"}`}
        >
          {actions?.map((item, key) => {
            return item?.component ? (
              <Fragment key={key}>{item?.component()}</Fragment>
            ) : (
              <Button
                action={item.type}
                key={key}
                className={item.className}
                label={item.label}
                onClick={item.action}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};
